<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import InfoGrid from '../parts/InfoGrid.vue';
import StarRating from '../parts/StarRating.vue';
import { formatPrice, hasValue } from '../helpers';

const props = defineProps({
  source: { type: Object, required: true },
});

const { t, locale } = useI18n();

const website = computed(() => props.source.website || {});
const isSellerChat = computed(() => props.source.kind === 'seller_chat');
const seller = computed(() => website.value.seller || {});
const product = computed(
  () => website.value.product || props.source.product || {}
);

const pagePath = computed(() => {
  if (!website.value.page_url) return '';
  try {
    const url = new URL(website.value.page_url);
    return `${url.hostname.replace('www.', '')}${url.pathname}`;
  } catch (error) {
    return website.value.page_url;
  }
});

const supportDetails = computed(() => [
  {
    key: 'order',
    label: t('MULTICHAT.SOURCE.ORDER'),
    value: website.value.order_id,
    mono: true,
  },
  {
    key: 'city',
    label: t('MULTICHAT.SOURCE.WEBSITE.CITY'),
    value: website.value.city,
  },
  {
    key: 'device',
    label: t('MULTICHAT.SOURCE.WEBSITE.DEVICE'),
    value: website.value.device,
  },
  {
    key: 'page',
    label: t('MULTICHAT.SOURCE.WEBSITE.PAGE'),
    value: pagePath.value,
    href: website.value.page_url,
    wide: true,
  },
]);

const sellerDetails = computed(() => [
  {
    key: 'order',
    label: t('MULTICHAT.SOURCE.ORDER'),
    value: website.value.order_id,
    mono: true,
  },
  {
    key: 'city',
    label: t('MULTICHAT.SOURCE.WEBSITE.CITY'),
    value: website.value.city,
  },
  {
    key: 'page',
    label: t('MULTICHAT.SOURCE.WEBSITE.PAGE'),
    value: pagePath.value,
    href: website.value.page_url,
    wide: true,
  },
]);
</script>

<template>
  <div v-if="isSellerChat" class="flex flex-col gap-3">
    <div
      class="flex items-center gap-3 p-3 rounded-lg bg-n-violet-2 border border-n-violet-4"
    >
      <span
        class="grid flex-shrink-0 size-10 rounded-lg place-items-center bg-n-violet-9 text-white"
      >
        <span class="i-lucide-store size-5" />
      </span>
      <div class="flex flex-col min-w-0 flex-1">
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-n-violet-11"
        >
          {{ t('MULTICHAT.SOURCE.WEBSITE.SELLER') }}
        </span>
        <span class="text-[13px] font-semibold text-n-slate-12 truncate">
          {{ seller.name }}
        </span>
      </div>
      <StarRating
        v-if="hasValue(seller.rating)"
        :rating="Number(seller.rating)"
        size="sm"
        show-value
      />
    </div>
    <div
      v-if="product.name"
      class="flex items-center gap-3 px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak"
    >
      <span class="i-lucide-package size-5 text-n-slate-9 flex-shrink-0" />
      <a
        v-if="product.url"
        :href="product.url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex-1 min-w-0 text-[13px] font-medium text-n-slate-12 hover:underline truncate"
      >
        {{ product.name }}
      </a>
      <span
        v-else
        class="flex-1 min-w-0 text-[13px] font-medium text-n-slate-12 truncate"
      >
        {{ product.name }}
      </span>
      <span
        v-if="hasValue(product.price)"
        class="text-[13px] font-semibold text-n-slate-12 flex-shrink-0"
      >
        {{ formatPrice(product.price, locale) }}
      </span>
    </div>
    <InfoGrid :items="sellerDetails" />
  </div>
  <div v-else class="flex flex-col gap-3">
    <div
      class="flex items-center gap-3 p-3 rounded-lg bg-n-blue-2 border border-n-blue-4"
    >
      <span
        class="grid flex-shrink-0 size-10 rounded-lg place-items-center bg-n-blue-9 text-white"
      >
        <span class="i-lucide-life-buoy size-5" />
      </span>
      <div class="flex flex-col min-w-0 flex-1">
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-n-blue-11"
        >
          {{ t('MULTICHAT.SOURCE.KINDS.SUPPORT') }}
        </span>
        <span class="text-[13px] font-medium text-n-slate-12 truncate">
          {{ website.topic || t('MULTICHAT.SOURCE.WEBSITE.SUPPORT_SUBTITLE') }}
        </span>
      </div>
    </div>
    <InfoGrid :items="supportDetails" />
  </div>
</template>
